/** Library reads only DEEPSEEK_API_KEY from env; the CLI bridges config.json → env var. */

import { randomBytes } from "node:crypto";
import { mkdirSync, readFileSync } from "node:fs";
import { homedir } from "node:os";
import { dirname, isAbsolute, join, resolve } from "node:path";
import { z } from "zod";
import { type ThemeName, isThemeName, resolveThemeName } from "./cli/ui/theme/tokens.js";
import { atomicWriteSync } from "./core/atomic-write.js";
import type { LanguageCode } from "./i18n/types.js";
import {
  type IndexUserConfig,
  type ResolvedIndexConfig,
  resolveIndexConfig,
} from "./index/config.js";
import { type McpServerSpec, parseMcpSpec } from "./mcp/spec.js";
import { normalizeQQAllowlist, normalizeQQOpenId } from "./qq/access.js";
import {
  type NormalizedToolRateLimitConfig,
  type ToolRateLimitConfig,
  normalizeToolRateLimitConfig,
} from "./tools/rate-limit.js";

/** Single trust dial: review queues edits + gates shell; auto applies + gates shell; yolo skips both gates; plan blocks every non-readonly tool (write_file / edit_file / multi_edit / run_command) at dispatch. */
export type EditMode = "review" | "auto" | "yolo" | "plan";

export const DEFAULT_MODEL = "deepseek-v4-flash";

/** Models the official api.deepseek.com endpoint currently accepts. v3-era
 *  `deepseek-chat`/`deepseek-reasoner` are gone — sending them produces a 400. */
export const SUPPORTED_OFFICIAL_MODELS: readonly string[] = [
  "deepseek-v4-flash",
  "deepseek-v4-pro",
];

export type ReasoningEffort = "low" | "medium" | "high" | "max";

export const REASONING_EFFORT_VALUES: readonly ReasoningEffort[] = ["low", "medium", "high", "max"];

export function isReasoningEffort(value: unknown): value is ReasoningEffort {
  return value === "low" || value === "medium" || value === "high" || value === "max";
}

export type EngineeringLifecycleMode = "off" | "strict";
export type HistoryScrollMode = "auto" | "native" | "app";

export type EmbeddingProvider = "ollama" | "openai-compat";

export interface OllamaEmbeddingUserConfig {
  baseUrl?: string;
  model?: string;
}

export interface OpenAICompatEmbeddingUserConfig {
  baseUrl?: string;
  apiKey?: string;
  model?: string;
  extraBody?: Record<string, unknown>;
  batchSize?: number;
  timeoutMs?: number;
}

export interface SemanticEmbeddingUserConfig {
  provider?: EmbeddingProvider;
  ollama?: OllamaEmbeddingUserConfig;
  openaiCompat?: OpenAICompatEmbeddingUserConfig;
}

export interface ResolvedOllamaEmbeddingConfig {
  provider: "ollama";
  baseUrl: string;
  model: string;
  timeoutMs: number;
}

export interface ResolvedOpenAICompatEmbeddingConfig {
  provider: "openai-compat";
  baseUrl: string;
  apiKey: string;
  model: string;
  extraBody: Record<string, unknown>;
  timeoutMs: number;
  batchSize: number;
}

export type ResolvedEmbeddingConfig =
  | ResolvedOllamaEmbeddingConfig
  | ResolvedOpenAICompatEmbeddingConfig;

export interface SemanticEmbeddingConfigView {
  provider: EmbeddingProvider;
  ollama: {
    baseUrl: string;
    model: string;
  };
  openaiCompat: {
    baseUrl: string;
    apiKey: string;
    apiKeySet: boolean;
    model: string;
    extraBody: Record<string, unknown>;
    batchSize: number;
  };
}

export interface McpServerConfig {
  command?: string;
  args?: string[];
  env?: Record<string, string>;
  transport?: "stdio" | "sse" | "streamable-http";
  /** Claude `.mcp.json` alias for `transport`; `"http"` is treated as `"streamable-http"`. */
  type?: "stdio" | "sse" | "streamable-http" | "http";
  url?: string;
  headers?: Record<string, string>;
  disabled?: boolean;
}

export interface QQBotConfig {
  appId?: string;
  appSecret?: string;
  sandbox?: boolean;
  enabled?: boolean;
  ownerOpenId?: string;
  allowlist?: string[];
}

export interface PricingOverride {
  inputCacheHit?: number;
  inputCacheMiss?: number;
  output?: number;
}

export interface RateLimitConfig {
  /** Client-side self-throttle in requests/minute — paces outbound chat calls with a min-interval timer. NOT a DeepSeek-enforced limit: DeepSeek's actual cap is concurrency, not RPM (500 for v4-pro, 2500 for v4-flash, account-wide), surfaced as HTTP 429. Set this only to be a polite neighbor on shared infra; single-user CLI rarely needs it. */
  rpm?: number;
}

export interface ProxyConfig {
  /** Proxy URL (e.g. `http://127.0.0.1:7897`, `socks5://host:1080`). Takes precedence over HTTPS_PROXY / HTTP_PROXY / ALL_PROXY env vars when set, so desktop users on Windows can route through Clash without fighting GUI env-var propagation (issue #1868). */
  url?: string;
  /** Skip proxy detection entirely — equivalent to launching with `--no-proxy`. */
  disabled?: boolean;
  /** Additional NO_PROXY patterns (curl syntax). Additive on top of env NO_PROXY and the default DeepSeek-bypass whitelist. */
  noProxy?: string[];
  /** When false, route api.deepseek.com / *.deepseek.com through the proxy too (issue #1497 — corporate firewalls that block direct egress). Default true preserves the clash/v2ray US-exit-IP 403 fix. Env `REASONIX_PROXY_DEEPSEEK_DIRECT` overrides. */
  bypassDeepSeekDirect?: boolean;
}

export interface ReasonixConfig {
  apiKey?: string;
  baseUrl?: string;
  lang?: LanguageCode;
  /** Persisted DeepSeek model id — `/model <id>` and the dashboard model picker write through this. */
  model?: string;
  editMode?: EditMode;
  editModeHintShown?: boolean;
  mouseClipboardHintShown?: boolean;
  /** When false, skip the boot splash animation and show the main UI immediately. Default true. */
  banner?: boolean;
  reasoningEffort?: ReasoningEffort;
  /** Default workspace root for the desktop client. CLI uses cwd. */
  workspaceDir?: string;
  /** Last N workspace paths the desktop client has opened, most recent first. */
  recentWorkspaces?: string[];
  /** Desktop only — open tabs in tab order, each with its workspace dir, loaded session and focus, persisted so restart restores every tab and its conversation (issues #933, #1244). Empty/absent → boot with a single default tab. */
  desktopOpenTabs?: DesktopOpenTab[];
  /** Desktop only — `openWith` value for clicking file links. Empty/undefined = OS default app. Examples: "code", "cursor", "C:\\path\\to\\editor.exe". */
  editor?: string;
  theme?: ThemeName | "auto";
  /** Stored as `--mcp`-format strings so one parser handles both flag and config. */
  mcp?: string[];
  /** Names of servers in `mcp` to skip on bridge — see `/mcp disable <name>`. */
  mcpDisabled?: string[];
  /** Env overlay per MCP server name (matches the `name=` prefix of the spec). Stdio transports merge this over process.env; SSE/HTTP ignore it. */
  mcpEnv?: Record<string, Record<string, string>>;
  /** Canonical MCP server configuration — merges with and overrides legacy `mcp`/`mcpEnv`/`mcpDisabled`. */
  mcpServers?: Record<string, McpServerConfig>;
  session?: string | null;
  setupCompleted?: boolean;
  search?: boolean;
  /** Web search engine backend: "bing" (default, scrapes cn.bing.com), "searxng" (self-hosted SearXNG), "metaso" (Metaso API), "tavily" (LLM-friendly API, free tier), "perplexity" (Perplexity AI), "exa" (Exa API), "brave" (Brave Search API), or "ollama" (Ollama cloud web search). */
  webSearchEngine?:
    | "bing"
    | "searxng"
    | "metaso"
    | "tavily"
    | "perplexity"
    | "exa"
    | "brave"
    | "ollama";
  /** Base URL for SearXNG instance (default http://localhost:8080). */
  webSearchEndpoint?: string;
  /** Metaso API key. Falls back to METASO_API_KEY env var. */
  metasoApiKey?: string;
  /** Tavily API key. Falls back to TAVILY_API_KEY env var. No baked-in default — free tier is 1000/mo per account, sharing would burn out. */
  tavilyApiKey?: string;
  /** Perplexity API key. Falls back to PERPLEXITY_API_KEY env var. Get one at https://perplexity.ai/settings/api */
  perplexityApiKey?: string;
  /** Exa API key. Falls back to EXA_API_KEY env var. Free 1000/mo signup at https://exa.ai */
  exaApiKey?: string;
  /** Ollama cloud API key. Falls back to OLLAMA_API_KEY env var. Used for Ollama web_search/web_fetch. */
  ollamaApiKey?: string;
  /** Brave Search API key. Falls back to BRAVE_SEARCH_API_KEY env var. Free 2000/mo signup at https://brave.com/search/api/ */
  braveApiKey?: string;

  /** TUI mouse-wheel scrolling via SGR mouse tracking. Default true. Set false to fall back to native terminal drag-select for copy (then wheel is terminal-dependent — most terminals translate wheel→arrow in alt-screen, some don't). */
  mouseTracking?: boolean;
  /** Rows scrolled per single SGR mouse-wheel report. Default 1 — most terminals emit 2-5 reports per physical notch, so 1 already produces 2-5 rows per notch (#1419). Bump to 3-5 only if your terminal emits one report per notch and scrolling feels slow (#1494). Clamped to [1, 10]. */
  mouseWheelRows?: number;
  /** Chat-history scrolling: "native" leaves terminal scrollback in charge; "app" captures wheel/PgUp/PgDn/End inside the TUI; "auto" enables app mode for terminals with known jumpy native scrollback. */
  historyScrollMode?: HistoryScrollMode;
  dashboard?: {
    /** Whether the embedded dashboard auto-starts on launch. Default true. Set false to disable without passing --no-dashboard each time. */
    enabled?: boolean;
    /** Pin the embedded dashboard to a fixed port — required for stable SSH tunnels. 0/absent → ephemeral. */
    port?: number;
    /** Bind address (#968). Defaults to 127.0.0.1 (loopback only). Set to 0.0.0.0 / :: / a LAN IP to expose to other devices; the URL token is then the only auth, so keep it secret. */
    host?: string;
    /** Stable URL token (#968). If unset, a fresh token is minted each boot. Min 16 chars enforced at load time. */
    token?: string;
  };
  /** Thread-area visibility toggles. */
  thread?: {
    /** When false, suppresses the quiet inline dividers for fold / abort / rate-limit
     *  warnings (severity="high" from the kernel). Default true. */
    showSystemEvents?: boolean;
  };
  /** Per-field visibility toggles for the bottom status row. All default to true (visible). */
  statusBar?: {
    showBalance?: boolean;
    showSessionCost?: boolean;
    showTurnCost?: boolean;
    showCacheHit?: boolean;
    showCtxUsage?: boolean;
    showVersion?: boolean;
    showFeedbackHint?: boolean;
  };
  /** Preferred display currency for costs (e.g. "USD" or "CNY"). When unset, falls back
   *  to the wallet currency if available, then defaults to CNY. */
  costCurrency?: string;
  projects?: {
    [absoluteRootDir: string]: {
      shellAllowed?: string[];
      /** Project-scoped hooks are arbitrary shell commands; load only after explicit trust. */
      hooksTrusted?: boolean;
      /** Absolute directory prefixes the user pre-approved for outside-sandbox file access (#684). */
      pathAllowed?: string[];
    };
  };
  /** Issue #259 — user-configurable sensitive-path prefixes and filename patterns.
   *  Commands touching these paths are demoted to the confirm gate even when allowlisted. */
  sensitivePaths?: {
    /** Path prefixes (tilde-relative or absolute) that trigger confirmation. */
    prefixes?: string[];
    /** Glob-style filename patterns (matched against basename, case-insensitive). */
    patterns?: string[];
  };
  index?: IndexUserConfig;
  semantic?: SemanticEmbeddingUserConfig;
  skills?: {
    paths?: string[];
  };
  /** Per-skill model override for `runAs: subagent` skills, keyed by skill name. Empty / missing entry → spawn site's default. */
  subagentModels?: Record<string, "flash" | "pro">;
  /** Enable the `java_source` tool for finding and decompiling Java class source. Default off. */
  javaSource?: boolean;
  /** User-declared extensions to the built-in memory types (#709). Unknown types round-trip even without a declaration; declaring one lets you attach a default priority + lifecycle. */
  memory?: {
    customTypes?: CustomMemoryTypeConfig[];
  };
  pricingOverride?: Record<string, PricingOverride>;
  /** Per-app proxy override. Layered on top of HTTPS_PROXY / NO_PROXY env vars + the default DeepSeek-bypass whitelist. */
  proxy?: ProxyConfig;
  rateLimit?: RateLimitConfig;
  toolRateLimit?: ToolRateLimitConfig;
  /** Host-enforced engineering lifecycle. Defaults to off so opt-outs pay zero prefix cost. */
  engineeringLifecycle?: {
    mode?: EngineeringLifecycleMode;
  };
  filesystem?: {
    /** read_file flips to outline mode for files above this. Default 64 KiB — keeps the cache prefix slim while covering ~99% of source files. Raise to 524288 (512 KiB) for the pre-0.46.0 "trust the cache" behavior. */
    outlineThresholdBytes?: number;
  };
  /** QQ Bot configuration */
  qq?: QQBotConfig;
}

export interface CustomMemoryTypeConfig {
  name: string;
  description?: string;
  priority?: "low" | "medium" | "high";
  expires?: "project_end";
}

export interface MemoryTypeRegistryEntry {
  name: string;
  builtin: boolean;
  description?: string;
  priority?: "low" | "medium" | "high";
  expires?: "project_end";
}

const BUILTIN_TYPE_DOCS: Record<string, string> = {
  user: "role / skills / preferences",
  feedback: "corrections or confirmed approaches",
  project: "facts / decisions about the current work",
  reference: "pointers to external systems the user uses",
};

/** Resolve the merged registry of memory types — built-ins, overlaid by anything in `config.memory.customTypes`. */
export function loadMemoryTypeRegistry(
  cfg: ReasonixConfig = readConfig(),
): MemoryTypeRegistryEntry[] {
  const out: MemoryTypeRegistryEntry[] = [];
  for (const name of ["user", "feedback", "project", "reference"]) {
    out.push({ name, builtin: true, description: BUILTIN_TYPE_DOCS[name] });
  }
  const seen = new Set(out.map((e) => e.name));
  for (const raw of cfg.memory?.customTypes ?? []) {
    if (!raw || typeof raw.name !== "string") continue;
    const name = raw.name.trim();
    if (!name || !/^[a-zA-Z][a-zA-Z0-9_-]{0,31}$/.test(name)) continue;
    if (seen.has(name)) continue;
    seen.add(name);
    const entry: MemoryTypeRegistryEntry = { name, builtin: false };
    if (typeof raw.description === "string") entry.description = raw.description;
    if (raw.priority === "low" || raw.priority === "medium" || raw.priority === "high") {
      entry.priority = raw.priority;
    }
    if (raw.expires === "project_end") entry.expires = raw.expires;
    out.push(entry);
  }
  return out;
}

export function memoryTypeDefaults(
  typeName: string,
  cfg: ReasonixConfig = readConfig(),
): { priority?: "low" | "medium" | "high"; expires?: "project_end" } {
  const found = loadMemoryTypeRegistry(cfg).find((e) => e.name === typeName);
  if (!found) return {};
  const out: { priority?: "low" | "medium" | "high"; expires?: "project_end" } = {};
  if (found.priority) out.priority = found.priority;
  if (found.expires) out.expires = found.expires;
  return out;
}

export function loadMetasoApiKey(path: string = defaultConfigPath()): string | undefined {
  if (process.env.METASO_API_KEY) return process.env.METASO_API_KEY.trim();
  const cfg = readConfig(path).metasoApiKey;
  if (cfg && typeof cfg === "string" && cfg.trim()) return cfg.trim();
  return undefined;
}

/** Tavily API key — env > config > undefined. Returning undefined means the caller must error out with a clear "go get one at tavily.com" message; we deliberately ship no default because the free 1000/mo quota wouldn't survive being shared. */
export function loadTavilyApiKey(path: string = defaultConfigPath()): string | undefined {
  if (process.env.TAVILY_API_KEY) return process.env.TAVILY_API_KEY.trim();
  const cfg = readConfig(path).tavilyApiKey;
  if (cfg && typeof cfg === "string" && cfg.trim()) return cfg.trim();
  return undefined;
}

/** Perplexity API key — env > config > undefined. Get one at https://perplexity.ai/settings/api */
export function loadPerplexityApiKey(path: string = defaultConfigPath()): string | undefined {
  if (process.env.PERPLEXITY_API_KEY) return process.env.PERPLEXITY_API_KEY.trim();
  const cfg = readConfig(path).perplexityApiKey;
  if (cfg && typeof cfg === "string" && cfg.trim()) return cfg.trim();
  return undefined;
}

/** Exa API key — env > config > undefined. Free 1000/mo signup at https://exa.ai */
export function loadExaApiKey(path: string = defaultConfigPath()): string | undefined {
  if (process.env.EXA_API_KEY) return process.env.EXA_API_KEY.trim();
  const cfg = readConfig(path).exaApiKey;
  if (cfg && typeof cfg === "string" && cfg.trim()) return cfg.trim();
  return undefined;
}

/** Ollama cloud API key — env > config > undefined. */
export function loadOllamaApiKey(path: string = defaultConfigPath()): string | undefined {
  if (process.env.OLLAMA_API_KEY) return process.env.OLLAMA_API_KEY.trim();
  if (process.env.ollamaApiKey) return process.env.ollamaApiKey.trim();
  const cfg = readConfig(path).ollamaApiKey;
  if (cfg && typeof cfg === "string" && cfg.trim()) return cfg.trim();
  return undefined;
}

/** Brave Search API key — env > config > undefined. Free 2000/mo signup at https://brave.com/search/api/ */
export function loadBraveApiKey(path: string = defaultConfigPath()): string | undefined {
  if (process.env.BRAVE_SEARCH_API_KEY) return process.env.BRAVE_SEARCH_API_KEY.trim();
  if (process.env.BRAVE_API_KEY) return process.env.BRAVE_API_KEY.trim();
  const cfg = readConfig(path).braveApiKey;
  if (cfg && typeof cfg === "string" && cfg.trim()) return cfg.trim();
  return undefined;
}

const DEFAULT_OLLAMA_URL = "http://localhost:11434";
const DEFAULT_EMBED_MODEL = "nomic-embed-text";
const DEFAULT_TIMEOUT_MS = 180_000;
const DEFAULT_BATCH_SIZE = 10;

export function defaultConfigPath(): string {
  return join(homedir(), ".reasonix", "config.json");
}

const STRING_ARRAY_FIELDS: Array<readonly string[]> = [
  ["mcp"],
  ["mcpDisabled"],
  ["recentWorkspaces"],
  ["skills", "paths"],
];

const stringArraySchema = z.array(z.string());

function sanitizeStringArrayField(
  cfg: Record<string, unknown>,
  segments: readonly string[],
  filePath: string,
): void {
  if (segments.length === 0) return;
  let parent: Record<string, unknown> = cfg;
  for (let i = 0; i < segments.length - 1; i++) {
    const seg = segments[i] as string;
    const next = parent[seg];
    if (!next || typeof next !== "object" || Array.isArray(next)) return;
    parent = next as Record<string, unknown>;
  }
  const leaf = segments[segments.length - 1] as string;
  const value = parent[leaf];
  if (value === undefined) return;
  const fieldName = segments.join(".");
  if (!Array.isArray(value)) {
    console.warn(`reasonix: config "${filePath}" field "${fieldName}" is not an array — ignoring`);
    delete parent[leaf];
    return;
  }
  const parsed = stringArraySchema.safeParse(value);
  if (parsed.success) return;
  const filtered = value.filter((x): x is string => typeof x === "string");
  console.warn(
    `reasonix: config "${filePath}" field "${fieldName}" had ${value.length - filtered.length} non-string item(s) — dropped`,
  );
  parent[leaf] = filtered;
}

export function readConfig(path: string = defaultConfigPath()): ReasonixConfig {
  try {
    // Strip the UTF-8 BOM if a foreign writer left one in — Windows
    // PowerShell 5's `Set-Content -Encoding UTF8` and several text
    // editors emit `EF BB BF` at the head of the file. `JSON.parse`
    // refuses BOM-prefixed input and throws, which used to fall
    // through to `return {}` and silently nuke every saved field on
    // the next read-modify-write.
    const raw = readFileSync(path, "utf8").replace(/^\uFEFF/, "");
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      const cfg = parsed as Record<string, unknown>;
      for (const segments of STRING_ARRAY_FIELDS) {
        sanitizeStringArrayField(cfg, segments, path);
      }
      return cfg as ReasonixConfig;
    }
  } catch {
    /* missing or malformed → empty config */
  }
  return {};
}

/** Whether the dashboard auto-starts. Default true; only false when explicitly set in config. */
export function loadDashboardEnabled(
  noConfig = false,
  path: string = defaultConfigPath(),
): boolean {
  if (noConfig) return true;
  const v = readConfig(path).dashboard?.enabled;
  return v !== false;
}

/** Get-or-mint a 32-byte hex dashboard token, persisting on first call so subsequent CLI boots reuse it (URLs survive restarts). Returns the existing token if it's already ≥16 chars. */
export function ensureDashboardToken(path: string = defaultConfigPath()): string {
  const cfg = readConfig(path);
  const existing = cfg.dashboard?.token?.trim();
  if (existing && existing.length >= 16) return existing;
  const minted = randomBytes(32).toString("hex");
  const next: ReasonixConfig = { ...cfg, dashboard: { ...cfg.dashboard, token: minted } };
  writeConfig(next, path);
  return minted;
}

/** Persist the actual port the server bound to so the next boot reuses it (and falls back to ephemeral if it's taken). */
export function saveDashboardPort(port: number, path: string = defaultConfigPath()): void {
  if (!Number.isInteger(port) || port < 1 || port > 65535) return;
  const cfg = readConfig(path);
  if (cfg.dashboard?.port === port) return;
  const next: ReasonixConfig = { ...cfg, dashboard: { ...cfg.dashboard, port } };
  writeConfig(next, path);
}

/** Wipe the persisted dashboard token — next boot mints a fresh one. Used by `/dashboard reset-token`. */
export function clearDashboardToken(path: string = defaultConfigPath()): void {
  const cfg = readConfig(path);
  if (!cfg.dashboard?.token) return;
  const { token: _drop, ...rest } = cfg.dashboard;
  const next: ReasonixConfig = { ...cfg, dashboard: rest };
  writeConfig(next, path);
}

export function writeConfig(cfg: ReasonixConfig, path: string = defaultConfigPath()): void {
  mkdirSync(dirname(path), { recursive: true });
  // Atomic — write to a sibling tmp then rename. A torn write (process
  // killed mid-write, or another reader catching the file before
  // writeFileSync finished) used to leave a 0-byte or truncated
  // config.json, which readConfig would then parse as `{}` and the next
  // saveX would silently overwrite every other field with that empty
  // baseline (issue #1535).
  const tmp = `${path}.${process.pid}.tmp`;
  atomicWriteSync(path, JSON.stringify(cfg, null, 2), tmp);
}

/** Resolve the language from config file. */
export function loadLanguage(path: string = defaultConfigPath()): LanguageCode | undefined {
  return readConfig(path).lang;
}

export function mcpEnvFor(
  serverName: string | null | undefined,
  cfg: ReasonixConfig,
): Record<string, string> | undefined {
  if (!serverName) return undefined;
  const entry = cfg.mcpEnv?.[serverName];
  if (!entry) return undefined;
  // Coerce to string and drop empty values — JSON config could be sloppy.
  const filtered: Record<string, string> = {};
  for (const [k, v] of Object.entries(entry)) {
    if (typeof v === "string" && v.length > 0) filtered[k] = v;
  }
  return Object.keys(filtered).length > 0 ? filtered : undefined;
}

function inferMcpTransport(cfg: McpServerConfig): "stdio" | "sse" | "streamable-http" {
  // Claude's `.mcp.json` uses `type` and shortens `streamable-http` to `http`.
  const declared = cfg.transport ?? cfg.type;
  if (declared === "http") return "streamable-http";
  if (declared) return declared;
  const url = cfg.url?.trim() ?? "";
  if (/^streamable\+https?:\/\//i.test(url)) return "streamable-http";
  if (/^https?:\/\//i.test(url)) return "sse";
  return "stdio";
}

function normalizeStringRecord(value: unknown): Record<string, string> | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(value)) {
    if (typeof v === "string" && v.length > 0) out[k] = v;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

export function normalizeMcpConfig(cfg: ReasonixConfig, extraLegacy?: string[]): McpServerSpec[] {
  const result: McpServerSpec[] = [];
  const seen = new Set<string>();

  // 1. Legacy specs first.
  const disabledFromLegacy = new Set(cfg.mcpDisabled ?? []);
  const legacySpecs = extraLegacy && extraLegacy.length > 0 ? extraLegacy : (cfg.mcp ?? []);
  for (const raw of legacySpecs) {
    if (typeof raw !== "string") continue;
    try {
      const spec = parseMcpSpec(raw);
      const env = spec.name ? normalizeStringRecord(cfg.mcpEnv?.[spec.name]) : undefined;
      const disabled = spec.name ? disabledFromLegacy.has(spec.name) : false;
      if (spec.transport === "stdio") {
        result.push({ ...spec, env, disabled });
      } else if (spec.transport === "sse") {
        result.push({ ...spec, disabled });
      } else {
        result.push({ ...spec, disabled });
      }
      if (spec.name) seen.add(spec.name);
    } catch {
      /* skip invalid legacy specs */
    }
  }

  // 2. mcpServers objects override on name conflict.
  for (const [name, serverCfg] of Object.entries(cfg.mcpServers ?? {})) {
    if (!serverCfg || typeof serverCfg !== "object") continue;
    const transport = inferMcpTransport(serverCfg as McpServerConfig);
    const disabled = (serverCfg as McpServerConfig).disabled === true;
    if (transport === "stdio") {
      const env = normalizeStringRecord((serverCfg as McpServerConfig).env);
      const spec: McpServerSpec = {
        transport: "stdio",
        name,
        command: (serverCfg as McpServerConfig).command ?? "",
        args: (serverCfg as McpServerConfig).args ?? [],
        env,
        disabled,
      };
      if (seen.has(name)) {
        const idx = result.findIndex((s) => s.name === name);
        if (idx >= 0) result[idx] = spec;
      } else {
        seen.add(name);
        result.push(spec);
      }
    } else {
      let url = (serverCfg as McpServerConfig).url ?? "";
      const streamMatch = /^streamable\+(https?:\/\/.+)$/i.exec(url);
      if (streamMatch) url = streamMatch[1]!;
      const headers = normalizeStringRecord((serverCfg as McpServerConfig).headers);
      if (transport === "sse") {
        const spec: McpServerSpec = {
          transport: "sse",
          name,
          url,
          headers,
          disabled,
        };
        if (seen.has(name)) {
          const idx = result.findIndex((s) => s.name === name);
          if (idx >= 0) result[idx] = spec;
        } else {
          seen.add(name);
          result.push(spec);
        }
      } else {
        const spec: McpServerSpec = {
          transport: "streamable-http",
          name,
          url,
          headers,
          disabled,
        };
        if (seen.has(name)) {
          const idx = result.findIndex((s) => s.name === name);
          if (idx >= 0) result[idx] = spec;
        } else {
          seen.add(name);
          result.push(spec);
        }
      }
    }
  }

  return result;
}

/** Persist the language so it survives a relaunch. */
export function saveLanguage(lang: LanguageCode, path: string = defaultConfigPath()): void {
  const cfg = readConfig(path);
  cfg.lang = lang;
  writeConfig(cfg, path);
}

export interface ResolvedEndpoint {
  baseUrl: string | undefined;
  apiKey: string | undefined;
}

// DEEPSEEK_BASE_URL is the original name; DEEPSEEK_API_BASE_URL is accepted as an
// alias so users who copy the OPENAI_BASE_URL pattern land on a working name (#1876).
export function resolveBaseUrlEnv(): string | undefined {
  return process.env.DEEPSEEK_BASE_URL || process.env.DEEPSEEK_API_BASE_URL || undefined;
}

// (baseUrl, apiKey) is a tuple: whichever source defines baseUrl owns apiKey too,
// so a stale env DEEPSEEK_API_KEY doesn't bleed into a custom config baseUrl (#1631).
export function loadEndpoint(path: string = defaultConfigPath()): ResolvedEndpoint {
  const envBaseUrl = resolveBaseUrlEnv();
  if (envBaseUrl) {
    return { baseUrl: envBaseUrl, apiKey: process.env.DEEPSEEK_API_KEY };
  }
  const cfg = readConfig(path);
  if (cfg.baseUrl) {
    return { baseUrl: cfg.baseUrl, apiKey: cfg.apiKey };
  }
  return { baseUrl: undefined, apiKey: process.env.DEEPSEEK_API_KEY ?? cfg.apiKey };
}

export function loadApiKey(path: string = defaultConfigPath()): string | undefined {
  return loadEndpoint(path).apiKey;
}

export function loadBaseUrl(path: string = defaultConfigPath()): string | undefined {
  return loadEndpoint(path).baseUrl;
}

// Mirrors the resolved tuple into env so subprocess constructions see the same pair.
export function bridgeEndpointEnv(path: string = defaultConfigPath()): void {
  const ep = loadEndpoint(path);
  if (ep.apiKey) process.env.DEEPSEEK_API_KEY = ep.apiKey;
  if (ep.baseUrl) process.env.DEEPSEEK_BASE_URL = ep.baseUrl;
}

function isNonNegativeNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0;
}

export function loadPricingOverride(
  path: string = defaultConfigPath(),
): Record<string, PricingOverride> {
  const raw = readConfig(path).pricingOverride;
  if (!isPlainObject(raw)) return {};

  const result: Record<string, PricingOverride> = {};
  for (const [model, value] of Object.entries(raw)) {
    if (!isPlainObject(value)) continue;
    const pricing: PricingOverride = {};
    if (isNonNegativeNumber(value.inputCacheHit)) pricing.inputCacheHit = value.inputCacheHit;
    if (isNonNegativeNumber(value.inputCacheMiss)) pricing.inputCacheMiss = value.inputCacheMiss;
    if (isNonNegativeNumber(value.output)) pricing.output = value.output;
    if (Object.keys(pricing).length > 0) result[model] = pricing;
  }
  return result;
}

export function loadProxyConfig(path: string = defaultConfigPath()): ProxyConfig {
  const cfg = readConfig(path).proxy;
  if (!cfg || typeof cfg !== "object") return {};
  const out: ProxyConfig = {};
  if (typeof cfg.url === "string" && cfg.url.trim() !== "") out.url = cfg.url.trim();
  if (cfg.disabled === true) out.disabled = true;
  if (Array.isArray(cfg.noProxy)) {
    const entries = cfg.noProxy.filter(
      (p): p is string => typeof p === "string" && p.trim() !== "",
    );
    if (entries.length > 0) out.noProxy = entries;
  }
  if (typeof cfg.bypassDeepSeekDirect === "boolean") {
    out.bypassDeepSeekDirect = cfg.bypassDeepSeekDirect;
  }
  return out;
}

export function loadRateLimit(path: string = defaultConfigPath()): RateLimitConfig | undefined {
  const rpm = readConfig(path).rateLimit?.rpm;
  if (typeof rpm !== "number" || !Number.isInteger(rpm) || rpm <= 0) return undefined;
  return { rpm };
}

export function loadMouseWheelRows(path: string = defaultConfigPath()): number | undefined {
  const raw = readConfig(path).mouseWheelRows;
  if (typeof raw !== "number" || !Number.isInteger(raw) || raw < 1) return undefined;
  return Math.min(raw, 10);
}

export function loadHistoryScrollMode(path: string = defaultConfigPath()): HistoryScrollMode {
  const raw = readConfig(path).historyScrollMode;
  return raw === "native" || raw === "app" || raw === "auto" ? raw : "auto";
}

export function loadToolRateLimit(
  path: string = defaultConfigPath(),
): false | NormalizedToolRateLimitConfig {
  return normalizeToolRateLimitConfig(readConfig(path).toolRateLimit);
}

export function saveBaseUrl(url: string, path: string = defaultConfigPath()): void {
  const cfg = readConfig(path);
  const trimmed = url.trim();
  if (trimmed) {
    cfg.baseUrl = trimmed;
  } else {
    cfg.baseUrl = undefined;
  }
  writeConfig(cfg, path);
}

export interface SkillPathEntry {
  raw: string;
  resolved: string;
}

export function resolveSkillPath(raw: string, baseDir: string): string {
  const homeExpanded = expandCurrentUserHome(raw.trim());
  return resolve(isAbsolute(homeExpanded) ? homeExpanded : join(baseDir, homeExpanded));
}

export function normalizeSkillPathEntries(
  paths: readonly unknown[],
  baseDir: string,
): SkillPathEntry[] {
  const out: SkillPathEntry[] = [];
  const seen = new Set<string>();
  for (const value of paths) {
    if (typeof value !== "string") continue;
    const raw = value.trim();
    if (!raw) continue;
    const resolved = resolveSkillPath(raw, baseDir);
    const key = skillPathKey(resolved);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ raw, resolved });
  }
  return out;
}

export function normalizeSkillPaths(paths: readonly unknown[], baseDir: string): string[] {
  return normalizeSkillPathEntries(paths, baseDir).map((entry) => entry.raw);
}

export function resolveSkillPaths(paths: readonly unknown[], baseDir: string): string[] {
  return normalizeSkillPathEntries(paths, baseDir).map((entry) => entry.resolved);
}

function skillPathKey(path: string): string {
  return process.platform === "win32" ? path.toLowerCase() : path;
}

function expandCurrentUserHome(path: string): string {
  if (path === "~") return homedir();
  if (path.startsWith("~/") || path.startsWith("~\\")) return join(homedir(), path.slice(2));
  return path;
}

export function loadSkillPaths(
  baseDir: string = process.cwd(),
  path: string = defaultConfigPath(),
): string[] {
  const raw = readConfig(path).skills?.paths;
  return Array.isArray(raw) ? normalizeSkillPaths(raw, baseDir) : [];
}

export function loadResolvedSkillPaths(
  baseDir: string = process.cwd(),
  path: string = defaultConfigPath(),
): string[] {
  const raw = readConfig(path).skills?.paths;
  return Array.isArray(raw) ? resolveSkillPaths(raw, baseDir) : [];
}

export function saveSkillPaths(
  paths: readonly unknown[],
  baseDir: string = process.cwd(),
  path: string = defaultConfigPath(),
): string[] {
  const cfg = readConfig(path);
  const normalized = normalizeSkillPaths(paths, baseDir);
  cfg.skills = { ...(cfg.skills ?? {}), paths: normalized };
  writeConfig(cfg, path);
  return normalized;
}

export function loadSubagentModels(
  path: string = defaultConfigPath(),
): Record<string, "flash" | "pro"> {
  const raw = readConfig(path).subagentModels;
  if (!raw || typeof raw !== "object") return {};
  const out: Record<string, "flash" | "pro"> = {};
  for (const [name, value] of Object.entries(raw)) {
    if (value === "flash" || value === "pro") out[name] = value;
  }
  return out;
}

export function saveSubagentModels(
  map: Record<string, "flash" | "pro">,
  path: string = defaultConfigPath(),
): void {
  const cfg = readConfig(path);
  const out: Record<string, "flash" | "pro"> = {};
  for (const [name, value] of Object.entries(map)) {
    if (value === "flash" || value === "pro") out[name] = value;
  }
  cfg.subagentModels = Object.keys(out).length > 0 ? out : undefined;
  writeConfig(cfg, path);
}

export function addSkillPath(
  skillPath: string,
  baseDir: string = process.cwd(),
  path: string = defaultConfigPath(),
): { added: boolean; path: string; resolved: string; paths: string[] } | { error: string } {
  const entry = normalizeSkillPathEntries([skillPath], baseDir)[0];
  if (!entry) return { error: "skill path is empty" };
  const existing = loadSkillPaths(baseDir, path);
  const seen = new Set(resolveSkillPaths(existing, baseDir).map(skillPathKey));
  const key = skillPathKey(entry.resolved);
  if (seen.has(key))
    return { added: false, path: entry.raw, resolved: entry.resolved, paths: existing };
  const paths = saveSkillPaths([...existing, entry.raw], baseDir, path);
  return { added: true, path: entry.raw, resolved: entry.resolved, paths };
}

export function removeSkillPath(
  target: string,
  baseDir: string = process.cwd(),
  path: string = defaultConfigPath(),
): { removed: boolean; path?: string; resolved?: string; paths: string[] } {
  const existing = loadSkillPaths(baseDir, path);
  const trimmed = target.trim();
  if (!trimmed) return { removed: false, paths: existing };
  const existingEntries = normalizeSkillPathEntries(existing, baseDir);
  const idx = /^\d+$/.test(trimmed) ? Number.parseInt(trimmed, 10) - 1 : -1;
  let removeAt = idx >= 0 && idx < existing.length ? idx : -1;
  if (removeAt < 0) {
    const targetEntry = normalizeSkillPathEntries([trimmed], baseDir)[0];
    const targetKey = targetEntry ? skillPathKey(targetEntry.resolved) : undefined;
    removeAt = existingEntries.findIndex(
      (entry) =>
        entry.raw === trimmed ||
        (targetKey !== undefined && skillPathKey(entry.resolved) === targetKey),
    );
  }
  if (removeAt < 0) return { removed: false, paths: existing };
  const removed = existingEntries[removeAt];
  const paths = saveSkillPaths(
    existing.filter((_, i) => i !== removeAt),
    baseDir,
    path,
  );
  return {
    removed: true,
    path: removed?.raw ?? existing[removeAt],
    resolved: removed?.resolved,
    paths,
  };
}

export function searchEnabled(path: string = defaultConfigPath()): boolean {
  const env = process.env.REASONIX_SEARCH;
  if (env === "off" || env === "false" || env === "0") return false;
  const cfg = readConfig(path).search;
  if (cfg === false) return false;
  return true;
}

export function loadJavaSourceEnabled(path: string = defaultConfigPath()): boolean {
  const env = process.env.REASONIX_JAVA_SOURCE;
  if (env === "1" || env === "true") return true;
  const cfg = readConfig(path).javaSource;
  return cfg === true;
}

export function webSearchEngine(
  path: string = defaultConfigPath(),
): "bing" | "searxng" | "metaso" | "tavily" | "perplexity" | "exa" | "brave" | "ollama" {
  const cfg = readConfig(path).webSearchEngine;
  if (cfg === "searxng") return "searxng";
  if (cfg === "metaso") return "metaso";
  if (cfg === "tavily") return "tavily";
  if (cfg === "perplexity") return "perplexity";
  if (cfg === "exa") return "exa";
  if (cfg === "brave") return "brave";
  if (cfg === "ollama") return "ollama";
  // Any other value (including legacy "mojeek" from configs predating the
  // engine swap) falls through to bing. Read-only — we never rewrite the
  // user's config, so `/search-engine mojeek` later still rejects loudly.
  return "bing";
}

export function webSearchEndpoint(path: string = defaultConfigPath()): string {
  const cfg = readConfig(path).webSearchEndpoint;
  if (cfg && typeof cfg === "string") return cfg;
  return "http://localhost:8080";
}

export function saveApiKey(key: string, path: string = defaultConfigPath()): void {
  const cfg = readConfig(path);
  const trimmed = key.trim();
  cfg.apiKey = trimmed;
  writeConfig(cfg, path);
  // A stale process env (User-level Windows env, `.env`, shell rc) shadows config in
  // loadEndpoint's fallback branch — an explicit UI save must win for the current run.
  if (trimmed) process.env.DEEPSEEK_API_KEY = trimmed;
}

/** Windows: case-insensitive — NTFS treats `F:\Foo` and `f:\foo` as one directory (#402). */
function findProjectKey(cfg: ReasonixConfig, rootDir: string): string | undefined {
  const projects = cfg.projects;
  if (!projects) return undefined;
  if (Object.hasOwn(projects, rootDir)) return rootDir;
  if (process.platform !== "win32") return undefined;
  const lower = rootDir.toLowerCase();
  for (const k of Object.keys(projects)) {
    if (k.toLowerCase() === lower) return k;
  }
  return undefined;
}

export function loadProjectShellAllowed(
  rootDir: string,
  path: string = defaultConfigPath(),
): string[] {
  const cfg = readConfig(path);
  const key = findProjectKey(cfg, rootDir);
  if (key === undefined) return [];
  return cfg.projects?.[key]?.shellAllowed ?? [];
}

export function addProjectShellAllowed(
  rootDir: string,
  prefix: string,
  path: string = defaultConfigPath(),
): void {
  const trimmed = prefix.trim();
  if (!trimmed) return;
  const cfg = readConfig(path);
  if (!cfg.projects) cfg.projects = {};
  const key = findProjectKey(cfg, rootDir) ?? rootDir;
  if (!cfg.projects[key]) cfg.projects[key] = {};
  const existing = cfg.projects[key].shellAllowed ?? [];
  if (existing.includes(trimmed)) return;
  cfg.projects[key].shellAllowed = [...existing, trimmed];
  writeConfig(cfg, path);
}

/** Match is exact after trim — NOT prefix-match: removing `git` MUST NOT drop `git push origin main`. */
export function removeProjectShellAllowed(
  rootDir: string,
  prefix: string,
  path: string = defaultConfigPath(),
): boolean {
  const trimmed = prefix.trim();
  if (!trimmed) return false;
  const cfg = readConfig(path);
  const key = findProjectKey(cfg, rootDir);
  if (key === undefined) return false;
  const existing = cfg.projects?.[key]?.shellAllowed ?? [];
  if (!existing.includes(trimmed)) return false;
  const next = existing.filter((p) => p !== trimmed);
  if (!cfg.projects) cfg.projects = {};
  if (!cfg.projects[key]) cfg.projects[key] = {};
  cfg.projects[key].shellAllowed = next;
  writeConfig(cfg, path);
  return true;
}

export function clearProjectShellAllowed(
  rootDir: string,
  path: string = defaultConfigPath(),
): number {
  const cfg = readConfig(path);
  const key = findProjectKey(cfg, rootDir);
  if (key === undefined) return 0;
  const existing = cfg.projects?.[key]?.shellAllowed ?? [];
  if (existing.length === 0) return 0;
  if (!cfg.projects) cfg.projects = {};
  if (!cfg.projects[key]) cfg.projects[key] = {};
  cfg.projects[key].shellAllowed = [];
  writeConfig(cfg, path);
  return existing.length;
}

export function projectHooksTrusted(rootDir: string, path: string = defaultConfigPath()): boolean {
  const cfg = readConfig(path);
  const key = findProjectKey(cfg, rootDir);
  return key !== undefined && cfg.projects?.[key]?.hooksTrusted === true;
}

export function trustProjectHooks(rootDir: string, path: string = defaultConfigPath()): void {
  const cfg = readConfig(path);
  if (!cfg.projects) cfg.projects = {};
  const key = findProjectKey(cfg, rootDir) ?? rootDir;
  if (!cfg.projects[key]) cfg.projects[key] = {};
  if (cfg.projects[key].hooksTrusted === true) return;
  cfg.projects[key].hooksTrusted = true;
  writeConfig(cfg, path);
}

export function loadProjectPathAllowed(
  rootDir: string,
  path: string = defaultConfigPath(),
): string[] {
  const cfg = readConfig(path);
  const key = findProjectKey(cfg, rootDir);
  if (key === undefined) return [];
  return cfg.projects?.[key]?.pathAllowed ?? [];
}

export function addProjectPathAllowed(
  rootDir: string,
  prefix: string,
  path: string = defaultConfigPath(),
): void {
  const trimmed = prefix.trim();
  if (!trimmed) return;
  const cfg = readConfig(path);
  if (!cfg.projects) cfg.projects = {};
  const key = findProjectKey(cfg, rootDir) ?? rootDir;
  if (!cfg.projects[key]) cfg.projects[key] = {};
  const existing = cfg.projects[key].pathAllowed ?? [];
  if (existing.includes(trimmed)) return;
  cfg.projects[key].pathAllowed = [...existing, trimmed];
  writeConfig(cfg, path);
}

export function removeProjectPathAllowed(
  rootDir: string,
  prefix: string,
  path: string = defaultConfigPath(),
): boolean {
  const trimmed = prefix.trim();
  if (!trimmed) return false;
  const cfg = readConfig(path);
  const key = findProjectKey(cfg, rootDir);
  if (key === undefined) return false;
  const existing = cfg.projects?.[key]?.pathAllowed ?? [];
  if (!existing.includes(trimmed)) return false;
  const next = existing.filter((p) => p !== trimmed);
  if (!cfg.projects) cfg.projects = {};
  if (!cfg.projects[key]) cfg.projects[key] = {};
  cfg.projects[key].pathAllowed = next;
  writeConfig(cfg, path);
  return true;
}

export function clearProjectPathAllowed(
  rootDir: string,
  path: string = defaultConfigPath(),
): number {
  const cfg = readConfig(path);
  const key = findProjectKey(cfg, rootDir);
  if (key === undefined) return 0;
  const existing = cfg.projects?.[key]?.pathAllowed ?? [];
  if (existing.length === 0) return 0;
  if (!cfg.projects) cfg.projects = {};
  if (!cfg.projects[key]) cfg.projects[key] = {};
  cfg.projects[key].pathAllowed = [];
  writeConfig(cfg, path);
  return existing.length;
}

/** Unknown values fall back to "review" so hand-edited bad config gets the safe default. */
export function loadEditMode(path: string = defaultConfigPath()): EditMode {
  const v = readConfig(path).editMode;
  if (v === "auto" || v === "yolo" || v === "plan") return v;
  return "review";
}

/** Persist the edit mode so `/mode auto` survives a relaunch. */
export function saveEditMode(mode: EditMode, path: string = defaultConfigPath()): void {
  const cfg = readConfig(path);
  cfg.editMode = mode;
  writeConfig(cfg, path);
}

/** Unknown values fall back to "off" so bad config keeps the zero-cost default. */
export function loadEngineeringLifecycleMode(
  path: string = defaultConfigPath(),
): EngineeringLifecycleMode {
  const v = readConfig(path).engineeringLifecycle?.mode;
  if (v === "off" || v === "strict") return v;
  return "off";
}

/** Bytes above which `read_file` flips to outline mode. Returns `undefined` so callers can apply the registered default; non-positive / non-numeric config values fall through to the default too. */
export function loadFilesystemOutlineThresholdBytes(
  path: string = defaultConfigPath(),
): number | undefined {
  const v = readConfig(path).filesystem?.outlineThresholdBytes;
  if (typeof v !== "number" || !Number.isFinite(v) || v <= 0) return undefined;
  return Math.floor(v);
}

/** True when the onboarding tip for the review/AUTO gate has been shown. */
export function editModeHintShown(path: string = defaultConfigPath()): boolean {
  return readConfig(path).editModeHintShown === true;
}

/** True when the mouse-tracking + clipboard tip has been shown. */
export function mouseClipboardHintShown(path: string = defaultConfigPath()): boolean {
  return readConfig(path).mouseClipboardHintShown === true;
}

/** Unknown / missing fall back to "high" — the only value every OpenAI-compatible endpoint accepts (vLLM rejects "max"). */
export function loadReasoningEffort(path: string = defaultConfigPath()): ReasoningEffort {
  const v = readConfig(path).reasoningEffort;
  return isReasoningEffort(v) ? v : "high";
}

export function loadTheme(path: string = defaultConfigPath()): ThemeName | "auto" | undefined {
  const value = readConfig(path).theme;
  if (value === "auto") return "auto";
  if (typeof value === "string" && isThemeName(value)) return value;
  return undefined;
}

export function resolveThemePreference(
  configTheme: ThemeName | "auto" | undefined,
  envTheme?: string | null,
): ThemeName {
  if (configTheme && configTheme !== "auto") return configTheme;
  return resolveThemeName(envTheme);
}

export function saveTheme(theme: ThemeName | "auto", path: string = defaultConfigPath()): void {
  const cfg = readConfig(path);
  cfg.theme = theme;
  writeConfig(cfg, path);
}

/** Persist the reasoning_effort cap so `/effort high` survives a relaunch. */
export function saveReasoningEffort(
  effort: ReasoningEffort,
  path: string = defaultConfigPath(),
): void {
  const cfg = readConfig(path);
  cfg.reasoningEffort = effort;
  writeConfig(cfg, path);
}

export function loadModel(path: string = defaultConfigPath()): string {
  const cfg = readConfig(path);
  const raw = cfg.model;
  const trimmed = typeof raw === "string" ? raw.trim() : "";
  if (!trimmed) return DEFAULT_MODEL;
  // Custom-endpoint owners pick their own model namespace; trust them.
  const customEndpoint = cfg.baseUrl?.trim() || resolveBaseUrlEnv();
  if (customEndpoint) return trimmed;
  return SUPPORTED_OFFICIAL_MODELS.includes(trimmed) ? trimmed : DEFAULT_MODEL;
}

export function saveModel(model: string, path: string = defaultConfigPath()): void {
  const trimmed = model.trim();
  if (!trimmed) return;
  const cfg = readConfig(path);
  cfg.model = trimmed;
  writeConfig(cfg, path);
}

export function loadWorkspaceDir(path: string = defaultConfigPath()): string | undefined {
  const v = readConfig(path).workspaceDir;
  return typeof v === "string" && v.trim() ? v : undefined;
}

export function saveWorkspaceDir(dir: string, path: string = defaultConfigPath()): void {
  const cfg = readConfig(path);
  const trimmed = dir.trim();
  if (trimmed) cfg.workspaceDir = trimmed;
  else cfg.workspaceDir = undefined;
  writeConfig(cfg, path);
}

export function loadEditor(path: string = defaultConfigPath()): string | undefined {
  const v = readConfig(path).editor;
  return typeof v === "string" && v.trim() ? v : undefined;
}

export function saveEditor(editor: string, path: string = defaultConfigPath()): void {
  const cfg = readConfig(path);
  const trimmed = editor.trim();
  if (trimmed) cfg.editor = trimmed;
  else cfg.editor = undefined;
  writeConfig(cfg, path);
}

/** Default true — quiet inline dividers (fold / abort / rate-limit) are valuable
 *  for transparency; users opt out only if they want a fully clean thread. */
export function loadShowSystemEvents(path: string = defaultConfigPath()): boolean {
  return readConfig(path).thread?.showSystemEvents !== false;
}

export function saveShowSystemEvents(on: boolean, path: string = defaultConfigPath()): void {
  const cfg = readConfig(path);
  cfg.thread = { ...(cfg.thread ?? {}), showSystemEvents: on };
  writeConfig(cfg, path);
}

export function loadRecentWorkspaces(path: string = defaultConfigPath()): string[] {
  const v = readConfig(path).recentWorkspaces;
  return Array.isArray(v) ? v.filter((s): s is string => typeof s === "string") : [];
}

const MAX_RECENT_WORKSPACES = 8;
export function pushRecentWorkspace(dir: string, path: string = defaultConfigPath()): void {
  const trimmed = dir.trim();
  if (!trimmed) return;
  const cfg = readConfig(path);
  const list = (cfg.recentWorkspaces ?? []).filter((s) => s !== trimmed);
  list.unshift(trimmed);
  cfg.recentWorkspaces = list.slice(0, MAX_RECENT_WORKSPACES);
  writeConfig(cfg, path);
}

/** Desktop only — one open tab's restorable state. */
export interface DesktopOpenTab {
  dir: string;
  /** Session the tab had loaded; reopened on boot if its jsonl still exists. */
  session?: string;
  /** Whether this was the focused tab. */
  active?: boolean;
}

export function loadDesktopOpenTabs(path: string = defaultConfigPath()): DesktopOpenTab[] {
  const v: unknown = readConfig(path).desktopOpenTabs;
  if (!Array.isArray(v)) return [];
  const out: DesktopOpenTab[] = [];
  for (const entry of v) {
    // Legacy format (issue #933) persisted bare workspace-dir strings.
    if (typeof entry === "string") {
      if (entry) out.push({ dir: entry });
    } else if (
      entry &&
      typeof entry === "object" &&
      typeof (entry as DesktopOpenTab).dir === "string" &&
      (entry as DesktopOpenTab).dir.length > 0
    ) {
      const e = entry as DesktopOpenTab;
      out.push({ dir: e.dir, session: e.session, active: e.active });
    }
  }
  return out;
}

export function saveDesktopOpenTabs(
  tabs: DesktopOpenTab[],
  path: string = defaultConfigPath(),
): void {
  const cfg = readConfig(path);
  const cleaned = tabs
    .filter((t) => t && typeof t.dir === "string" && t.dir.length > 0)
    .map((t) => {
      const e: DesktopOpenTab = { dir: t.dir };
      if (t.session) e.session = t.session;
      if (t.active) e.active = true;
      return e;
    });
  cfg.desktopOpenTabs = cleaned.length === 0 ? undefined : cleaned;
  writeConfig(cfg, path);
}

export function loadIndexUserConfig(path: string = defaultConfigPath()): IndexUserConfig {
  return readConfig(path).index ?? {};
}

export function loadIndexConfig(path: string = defaultConfigPath()): ResolvedIndexConfig {
  return resolveIndexConfig(readConfig(path).index);
}

export function saveIndexConfig(user: IndexUserConfig, path: string = defaultConfigPath()): void {
  const cfg = readConfig(path);
  cfg.index = user;
  writeConfig(cfg, path);
}

export function loadSemanticEmbeddingUserConfig(
  path: string = defaultConfigPath(),
): SemanticEmbeddingUserConfig {
  return normalizeSemanticEmbeddingUserConfig(readConfig(path).semantic);
}

export function saveSemanticEmbeddingConfig(
  user: SemanticEmbeddingUserConfig,
  path: string = defaultConfigPath(),
): void {
  const cfg = readConfig(path);
  cfg.semantic = normalizeSemanticEmbeddingUserConfig(user);
  writeConfig(cfg, path);
}

export function resolveSemanticEmbeddingConfig(
  path: string = defaultConfigPath(),
): ResolvedEmbeddingConfig {
  const user = loadSemanticEmbeddingUserConfig(path);
  const provider = user.provider ?? "ollama";
  if (provider === "openai-compat") {
    const baseUrl = user.openaiCompat?.baseUrl?.trim() ?? "";
    const apiKey = user.openaiCompat?.apiKey?.trim() ?? "";
    const model = user.openaiCompat?.model?.trim() ?? "";
    if (!baseUrl) throw new Error("OpenAI-compatible embeddings require an API URL.");
    requireValidUrl(baseUrl, "OpenAI-compatible API URL");
    if (!apiKey) throw new Error("OpenAI-compatible embeddings require an API key.");
    if (!model) throw new Error("OpenAI-compatible embeddings require a model.");
    return {
      provider,
      baseUrl,
      apiKey,
      model,
      extraBody: normalizeExtraBody(user.openaiCompat?.extraBody),
      timeoutMs: user.openaiCompat?.timeoutMs ?? DEFAULT_TIMEOUT_MS,
      batchSize: user.openaiCompat?.batchSize ?? DEFAULT_BATCH_SIZE,
    };
  }
  return {
    provider: "ollama",
    baseUrl: user.ollama?.baseUrl?.trim() || process.env.OLLAMA_URL || DEFAULT_OLLAMA_URL,
    model: user.ollama?.model?.trim() || process.env.REASONIX_EMBED_MODEL || DEFAULT_EMBED_MODEL,
    timeoutMs: DEFAULT_TIMEOUT_MS,
  };
}

export function redactSemanticEmbeddingConfig(
  user: SemanticEmbeddingUserConfig,
): SemanticEmbeddingConfigView {
  const normalized = normalizeSemanticEmbeddingUserConfig(user);
  return {
    provider: normalized.provider ?? "ollama",
    ollama: {
      baseUrl: normalized.ollama?.baseUrl?.trim() || process.env.OLLAMA_URL || DEFAULT_OLLAMA_URL,
      model:
        normalized.ollama?.model?.trim() || process.env.REASONIX_EMBED_MODEL || DEFAULT_EMBED_MODEL,
    },
    openaiCompat: {
      baseUrl: normalized.openaiCompat?.baseUrl?.trim() ?? "",
      apiKey: normalized.openaiCompat?.apiKey ? redactKey(normalized.openaiCompat.apiKey) : "",
      apiKeySet: Boolean(normalized.openaiCompat?.apiKey?.trim()),
      model: normalized.openaiCompat?.model?.trim() ?? "",
      extraBody: normalizeExtraBody(normalized.openaiCompat?.extraBody),
      batchSize: normalized.openaiCompat?.batchSize ?? DEFAULT_BATCH_SIZE,
    },
  };
}

/** Mark the onboarding tip as shown so subsequent launches skip it. */
export function markEditModeHintShown(path: string = defaultConfigPath()): void {
  const cfg = readConfig(path);
  if (cfg.editModeHintShown === true) return;
  cfg.editModeHintShown = true;
  writeConfig(cfg, path);
}

/** Mark the mouse + clipboard tip as shown. */
export function markMouseClipboardHintShown(path: string = defaultConfigPath()): void {
  const cfg = readConfig(path);
  if (cfg.mouseClipboardHintShown === true) return;
  cfg.mouseClipboardHintShown = true;
  writeConfig(cfg, path);
}

/** Self-hosted DeepSeek-compatible endpoints may issue any token shape, so we only typo-guard here — the real auth check is the first API call against `baseUrl`. */
export function isPlausibleKey(key: string): boolean {
  const trimmed = key.trim();
  if (trimmed.length < 16) return false;
  return !/\s/.test(trimmed);
}

/** Mask a key for display: `sk-abcd...wxyz`. */
export function redactKey(key: string): string {
  if (!key) return "";
  if (key.length <= 12) return "****";
  return `${key.slice(0, 6)}…${key.slice(-4)}`;
}

function normalizeSemanticEmbeddingUserConfig(
  cfg: SemanticEmbeddingUserConfig | undefined,
): SemanticEmbeddingUserConfig {
  return {
    provider: cfg?.provider === "openai-compat" ? "openai-compat" : "ollama",
    ollama: {
      baseUrl: normalizeOptionalString(cfg?.ollama?.baseUrl),
      model: normalizeOptionalString(cfg?.ollama?.model),
    },
    openaiCompat: {
      baseUrl: normalizeOptionalString(cfg?.openaiCompat?.baseUrl),
      apiKey: normalizeOptionalString(cfg?.openaiCompat?.apiKey),
      model: normalizeOptionalString(cfg?.openaiCompat?.model),
      extraBody: normalizeExtraBody(cfg?.openaiCompat?.extraBody),
      timeoutMs: normalizePositiveInt(cfg?.openaiCompat?.timeoutMs),
      batchSize: normalizePositiveInt(cfg?.openaiCompat?.batchSize),
    },
  };
}

function normalizeOptionalString(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function normalizePositiveInt(value: number | undefined): number | undefined {
  return value !== undefined && Number.isInteger(value) && value > 0 ? value : undefined;
}

function normalizeExtraBody(value: Record<string, unknown> | undefined): Record<string, unknown> {
  if (value === undefined) return {};
  if (!isPlainObject(value)) {
    throw new Error("Semantic embedding extraBody must be a JSON object.");
  }
  return { ...value };
}

function requireValidUrl(value: string, label: string): void {
  try {
    new URL(value);
  } catch {
    throw new Error(`${label} must be a valid URL.`);
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

export interface LoadedQQConfig {
  appId?: string;
  appSecret?: string;
  sandbox?: boolean;
  enabled?: boolean;
  ownerOpenId?: string;
  allowlist?: string[];
}

export function loadQQConfig(path: string = defaultConfigPath()): LoadedQQConfig {
  const envSandbox = process.env.QQ_SANDBOX;
  const envAllowlist = normalizeQQAllowlist(process.env.QQ_ALLOWLIST);
  const fromEnv = {
    appId: process.env.QQ_APPID,
    appSecret: process.env.QQ_SECRET,
    sandbox: envSandbox === "1" ? true : envSandbox === "0" ? false : undefined,
    ownerOpenId: normalizeQQOpenId(process.env.QQ_OWNER_OPENID),
    allowlist: envAllowlist,
  };
  const fromCfg = readConfig(path).qq ?? {};
  const ownerOpenId = fromEnv.ownerOpenId ?? normalizeQQOpenId(fromCfg.ownerOpenId);
  const allowlist = normalizeQQAllowlist(fromEnv.allowlist ?? fromCfg.allowlist)?.filter(
    (openid) => openid !== ownerOpenId,
  );
  return {
    appId: fromEnv.appId ?? fromCfg.appId,
    appSecret: fromEnv.appSecret ?? fromCfg.appSecret,
    sandbox: fromEnv.sandbox ?? fromCfg.sandbox ?? false,
    enabled: fromCfg.enabled === true,
    ownerOpenId,
    allowlist,
  };
}

export function saveQQConfig(cfg: LoadedQQConfig, path: string = defaultConfigPath()): void {
  const rootCfg = readConfig(path);
  const ownerOpenId = normalizeQQOpenId(cfg.ownerOpenId);
  const allowlist = normalizeQQAllowlist(cfg.allowlist)?.filter((openid) => openid !== ownerOpenId);
  rootCfg.qq = {
    appId: cfg.appId,
    appSecret: cfg.appSecret,
    sandbox: cfg.sandbox,
    enabled: cfg.enabled,
    ownerOpenId,
    allowlist,
  };
  writeConfig(rootCfg, path);
}
